import { useMemo } from 'react';

import { CPLAT_API_ROUTES, useFetch } from '@/api';
import type { CplatApiResponse } from '@/types/api';
import {
  type MembershipPaymentResult,
  resolveMembershipPaymentResult,
} from '@/utils/membership-payment-result';

type MembershipPaymentResultResponse = CplatApiResponse<
  MembershipPaymentResult | MembershipPaymentResult[]
>;

type UseMembershipPaymentResultParams = {
  cplatToken: string;
  paymentResultId?: string | null;
  channel: 'WEB_MOBILE' | 'WEB_PC';
  enabled?: boolean;
};

export const useMembershipPaymentResult = ({
  cplatToken,
  paymentResultId,
  channel,
  enabled = true,
}: UseMembershipPaymentResultParams) => {
  const params = useMemo(
    () => ({
      cplatToken,
      paymentResultId: paymentResultId ?? '',
      channel,
    }),
    [channel, cplatToken, paymentResultId],
  );

  const query = useFetch<MembershipPaymentResultResponse>(
    CPLAT_API_ROUTES.getMembershipPaymentResult,
    {
      method: 'POST',
      params,
      config: {
        enabled: enabled && Boolean(cplatToken && paymentResultId),
        retry: 1,
        staleTime: 30 * 1000,
        gcTime: 5 * 60 * 1000,
        throwOnError: false,
      },
    },
  );

  const paymentResult = useMemo(() => resolveMembershipPaymentResult(query.data), [query.data]);

  return {
    ...query,
    paymentResult,
  };
};
